import { computed, ref } from 'vue';
import { listSettingsUsers, saveSettingsUser } from '@/api/settingsApi';
import { useSettingsEntityList } from './useSettingsEntityList';

export function useSettingsUsers() {
  const { afterMutation } = useSettingsEntityList();

  const loading = ref(false);
  const saving = ref(false);
  const loadedOnce = ref(false);
  const error = ref(null);
  const users = ref([]);
  const roles = ref([]);
  const searchQuery = ref('');

  const filteredUsers = computed(() => {
    const q = searchQuery.value.trim().toLowerCase();
    if (!q) return users.value;
    return users.value.filter((u) => {
      const hay = [u.fullName, u.username, u.email, u.role]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  });

  const countLabel = computed(() => {
    const n = filteredUsers.value.length;
    const total = users.value.length;
    if (searchQuery.value.trim() && n !== total) {
      return `${n} of ${total} users`;
    }
    return `${total} user${total === 1 ? '' : 's'}`;
  });

  async function loadUsers() {
    loading.value = true;
    error.value = null;
    try {
      const data = await listSettingsUsers();
      users.value = Array.isArray(data?.users) ? data.users : [];
      roles.value = Array.isArray(data?.roles) ? data.roles : [];
      loadedOnce.value = true;
    } catch (e) {
      error.value = e?.message || 'Failed to load users';
    } finally {
      loading.value = false;
    }
  }

  async function saveUser(payload) {
    saving.value = true;
    error.value = null;
    try {
      const data = await saveSettingsUser(payload);
      await afterMutation(async () => {
        if (Array.isArray(data?.users)) {
          users.value = data.users;
        } else {
          await loadUsers();
        }
      });
      return data?.user || null;
    } catch (e) {
      error.value = e?.message || 'Failed to save user';
      throw e;
    } finally {
      saving.value = false;
    }
  }

  return {
    loading,
    saving,
    loadedOnce,
    error,
    users,
    roles,
    searchQuery,
    filteredUsers,
    countLabel,
    loadUsers,
    saveUser
  };
}
